import { getDocumentReferences } from '@ctw/shared/api/fhir/document';
import { EncounterModel } from '@ctw/shared/api/fhir/models/encounter';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import {
  type FqsRequestOptions,
  fqsRequestAll,
  MAX_OBJECTS_PER_REQUEST,
} from '@ctw/shared/api/fqs/client';
import {
  type EncounterGraphqlResponse,
  encountersQuery,
} from '@ctw/shared/api/fqs/queries/encounters';
import { getLensBuilderId } from '@ctw/shared/api/urls';
import type { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import type { Env } from '@ctw/shared/utils/environments';
import { FQS_HOTSTORAGE_REQUEST_OPTIONS } from '@ctw/shared/utils/hotstorage-header';
import type { GraphQLClient } from 'graphql-request';
import { SYSTEM_ZUS_OWNER, SYSTEM_ZUS_SUMMARY } from './system-urls';

// Fetches the patient's encounter Lens summaries: the tag filter mirrors the encounters-tab
// query (`hooks/use-encounters.ts`). Does not dedupe — the Lens groups source encounters into
// summaries server-side (an 8-hour window), so its output can't collide on the client merge key.
export async function getLensEncountersFQS(
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  patient: PatientModel,
  env: Env,
) {
  const { data } = await fqsRequestAll<EncounterGraphqlResponse>(
    telemetry,
    graphqlClient,
    encountersQuery,
    'EncounterConnection',
    {
      upid: patient.UPID,
      cursor: '',
      first: MAX_OBJECTS_PER_REQUEST,
      sort: {},
      filter: {
        tag: {
          allmatch: [
            `${SYSTEM_ZUS_SUMMARY}|Common`,
            `${SYSTEM_ZUS_OWNER}|builder/${getLensBuilderId(env)}`,
          ],
        },
      },
    },
    undefined,
    FQS_HOTSTORAGE_REQUEST_OPTIONS,
  );

  return data.EncounterConnection.edges
    .map((x) => x.node)
    .map((c) => new EncounterModel(c, c.ProvenanceList, undefined, c.BasicList));
}

export async function getEncountersById(
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  patient: PatientModel,
  ids: Array<string>,
  includeClinicalNotes = true,
  options?: FqsRequestOptions,
) {
  const { data } = await fqsRequestAll<EncounterGraphqlResponse>(
    telemetry,
    graphqlClient,
    encountersQuery,
    'EncounterConnection',
    {
      upid: patient.UPID,
      cursor: '',
      first: MAX_OBJECTS_PER_REQUEST,
      sort: {},
      filter: {
        ids: {
          anymatch: ids,
        },
      },
    },
    undefined,
    options,
  );

  const results = data.EncounterConnection.edges
    .map((x) => x.node)
    .map((c) => new EncounterModel(c, c.ProvenanceList, undefined, c.BasicList));

  if (includeClinicalNotes) {
    const documents = await getDocumentReferences(
      telemetry,
      graphqlClient,
      patient,
      [MAX_OBJECTS_PER_REQUEST],
      FQS_HOTSTORAGE_REQUEST_OPTIONS,
    );
    results.forEach((e) => {
      e.setClinicalNotesFromDocumentPool(documents);
    });
  }

  // Callers pass explicit ids and expect exactly those ids back — no dedupe/merge here, since
  // that could collapse two requested encounters together and drop one of the requested ids.
  return results;
}
